import React, { useMemo } from 'react';
import { HolidayGrouping } from "../../models/Holidays.model";
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import moment from "moment";
import { OrangeChip, OutlineChip, PrimaryChip } from '@shared/ui/atoms/Chip';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import styled from "styled-components";
import { ApprovalState } from '@features/holidays/models/approval-requests.models';
import { User } from '@shared/models/user.models';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectHolidayUserInfo } from '@features/holidays/store/holidays-v2.duck';
import { isCompanyAdminUser } from '@shared/utils/AccessControl';
import { LeaveTypeLabel } from "./LeaveTypeLabel";
import { HOLIDAY_ID } from '@features/whereabouts/services/WhereaboutOptions';

export function UsersHolidayBookingItem(props: Props) {
  const {holiday, currentUser, isLineReportOfCurrentUser} = props;
  const {t} = useTranslation();
  const userInfo = useSelector(selectHolidayUserInfo);

  const status = useMemo(() => {
    const isPending = holiday.holidayBookingList.find(h => h.state === ApprovalState.pending);
    const isApproved = holiday.holidayBookingList.find(h => h.state === ApprovalState.approved);
    return !!isPending ? ApprovalState.pending : isApproved ? ApprovalState.approved : '';
  }, [holiday]);

  const dateRange = useMemo(() => {
    return `${moment(holiday.fromDate).format('D MMMM')} - ${moment(holiday.toDate).format('D MMMM')}`
  }, [holiday]);

  const numberOfDays = useMemo(() => {
    return holiday.holidayBookingList.map(hd => {
      let count = 0;
      if (hd.am) count++;
      if (hd.pm) count++;
      return count;
    }).reduce((prev, curr) => prev + curr) / 2
  }, [holiday]);

  const showPendingChip = useMemo(() => {
    return currentUser?.companyEntity.holidayApprovalEnabled && status === ApprovalState.pending;
  }, [currentUser, status]);

  const showApprovedChip = useMemo(() => {
    return currentUser?.companyEntity.holidayApprovalEnabled && status === ApprovalState.approved;
  }, [currentUser, status]);

  const showEditLink = useMemo(() => {
    if (!currentUser?.companyEntity.enableHolidayBooking) {
      return false;
    }

    if (isCompanyAdminUser(currentUser) || isLineReportOfCurrentUser) {
      return true;
    }

    if (userInfo.userId === currentUser?.id) {
      return !moment(holiday.fromDate).isBefore(moment());
    }

    // Different user who is not line manager or company admin
    return false;
  }, [currentUser, holiday.fromDate, isLineReportOfCurrentUser, userInfo.userId]);

  const editLink = !!userInfo?.userId ? `/holidays/edit/${holiday.fromDate}/${userInfo?.userId}` : `/holidays/edit/${holiday.fromDate}/${currentUser?.id}`

  return (
    <>
      <SpaceBetweenRow>
        <LeaveTypeLabel whereaboutsId={HOLIDAY_ID} fallbackLabel={t('whereabouts-labels.HOLIDAY')} />
        {showEditLink && <IconButton icon={IconTypes.Edit} link={editLink}/>}
      </SpaceBetweenRow>
      <DateRange>{dateRange}</DateRange>
      <ChipRow>
        {showPendingChip && <OrangeChip size={'small'} label={'button.pending'} dataTest={"pending-chip"} onClick={() => {}} />}
        {showApprovedChip && <PrimaryChip size={'small'} label={'button.approved'} dataTest={"approved-chip"} onClick={() => {}} />}
        {numberOfDays > 0 && <OutlineChip label={t('holidays.holiday-duration', {duration: numberOfDays})} onClick={() => {}} size={'small'} />}
      </ChipRow>
    </>
  )
}

interface Props {
  holiday: HolidayGrouping;
  currentUser?: User;
  isLineReportOfCurrentUser: boolean;
}

export const SpaceBetweenRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const DateRange = styled(BodyRegular)`
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 18px;
`

const ChipRow = styled.div`
  display: flex;
  align-items: center;
  &>* {
    margin-right: 8px;
  }
  p {
    font-weight: 700;
  }
`
